/**
 * ConfirmVersionModal — подтверждение сохранения новой версии тарифа.
 * Эффекты: сколько клиентов затронет, уведомление в WhatsApp, применение со
 * следующего продления (окно 7 дней). §5.2. На десктопе — центр-модалка, на
 * мобилке — bottom-sheet; контент общий.
 */

import type {ReactNode} from 'react';
import {Calendar, Info, Loader2, MessageCircle, Users} from 'lucide-react';

import {SheetGrabber} from '@components/SheetGrabber';
import {Button} from '@components/ui/Button';
import {Dialog, DialogContent} from '@components/ui/Dialog';
import {Sheet, SheetContent} from '@components/ui/Sheet';

type ConfirmVersionModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Скольких клиентов затронет; null — BE посчитает после сохранения */
  affected: number | null;
  pending: boolean;
  mobile?: boolean;
  onConfirm: () => void;
};

export const ConfirmVersionModal = ({
  open,
  onOpenChange,
  affected,
  pending,
  mobile = false,
  onConfirm,
}: ConfirmVersionModalProps) => {
  const body = (
    <>
      {mobile && <SheetGrabber />}
      <div className="mb-3 grid size-[46px] place-items-center rounded-xl bg-skr-info-chip text-primary">
        <Info className="size-6" strokeWidth={1.8} />
      </div>
      <h3 className="text-[17px] font-bold">Сохранить новую версию тарифа?</h3>
      <p className="mt-2 text-[13.5px] leading-[1.5] text-muted-foreground">
        Создаётся новая версия. Прошлые версии сохраняются как основание для уже проведённых
        платежей.
      </p>

      <div className="mt-4 flex flex-col gap-[11px] rounded-xl border border-skr-line2 bg-skr-head px-4 py-3.5">
        <Effect icon={<Users className="size-[17px]" strokeWidth={1.8} />}>
          {affected === null ? (
            <>Коснётся всех клиентов линейки с изменением цены</>
          ) : (
            <>
              Затронет <b className="font-mono">{affected}</b> клиентов с изменением цены
            </>
          )}
        </Effect>
        <Effect icon={<MessageCircle className="size-[17px] text-skr-paid" strokeWidth={1.9} />}>
          Всем уйдёт уведомление в WhatsApp
        </Effect>
        <Effect icon={<Calendar className="size-[17px]" strokeWidth={1.8} />}>
          Применяется со следующего продления ·{' '}
          <span className="text-muted-foreground">
            у кого до продления &lt;7 дней — циклом позже
          </span>
        </Effect>
      </div>

      <div className="mt-5 flex gap-2.5">
        <Button
          variant="outline"
          className="h-12 flex-1 rounded-[12px]"
          disabled={pending}
          onClick={() => onOpenChange(false)}
        >
          Отмена
        </Button>
        <Button className="h-12 flex-1 rounded-[12px]" disabled={pending} onClick={onConfirm}>
          {pending ? <Loader2 className="size-4 animate-spin" /> : 'Сохранить и уведомить'}
        </Button>
      </div>
    </>
  );

  if (mobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" showClose={false} className="gap-0 rounded-t-[22px] p-5 pb-6">
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showClose={false}
        className="w-[460px] max-w-[calc(100vw-2rem)] gap-0 rounded-[18px] p-6"
      >
        {body}
      </DialogContent>
    </Dialog>
  );
};

const Effect = ({icon, children}: {icon: ReactNode; children: ReactNode}) => (
  <div className="flex items-center gap-2.5 text-[13px] text-skr-ink">
    <span className="shrink-0 text-muted-foreground">{icon}</span>
    <span>{children}</span>
  </div>
);
